import { MapBackgroundRenderer } from '../MapBackgroundRenderer';
import { createDomLoadingBar, type DomLoadingBar } from '../loadingBarDom';
import { LOADING_LAYOUT, loadingLogoWidthCss, LOGO_GLOW_FILTER } from '../loadingLayout';
import { createButton } from '../formControls';
import { GAME_FONT_FAMILY, SITE_URL } from '@shared/constants';
import type { CharacterSummary } from '../../api/AuthApi';
import {
  type AuthScreenType,
  type AuthBackgroundDef,
  getBackgroundForScreen,
  getBackgroundForCharacter,
  getRandomLoginBackground,
  getMapData,
} from '../AuthBackgroundConfig';
import type { AuthUI } from '../AuthUI';

/**
 * Chrome visual de la pantalla de auth: frame de ventana skineado, logo procesado, fondos de mapa
 * (con transición/fade), loading bars, diálogos (confirmar/opciones). Extraído de AuthUI (god-object).
 * Los CAMPOS de estado (mapBackgroundContainer/Renderer, currentBackground, loading bars, transitionBar)
 * viven en AuthUI (host) y se acceden vía `this.host.*`; AuthChrome aporta el comportamiento + statics.
 */
export class AuthChrome {
  /** Configuración actual del fondo (estado interno del chrome). */
  private currentBackground: AuthBackgroundDef | null = null;

  constructor(private host: AuthUI) {}

  // ---------------------------------------------------------------------------
  // Window skin (tiled center + repeated borders + fixed corners) - 64x64 pieces
  // ---------------------------------------------------------------------------
  private static readonly UI_WINDOW_TILE_PX = 64;
  private static readonly UI_WINDOW_ASSETS = {
    bgTile: '/assets/ui/bg_tile.png',
    borderTop: '/assets/ui/border_top.png',
    borderBottom: '/assets/ui/border_bottom.png',
    borderLeft: '/assets/ui/border_left.png',
    borderRight: '/assets/ui/border_right.png',
    cornerTL: '/assets/ui/corner_tl.png',
    cornerTR: '/assets/ui/corner_tr.png',
    // En este pack, las esquinas inferiores vienen como border_bl/br
    cornerBL: '/assets/ui/border_bl.png',
    cornerBR: '/assets/ui/border_br.png',
  } as const;

  private static readonly BG_FADE_MS = 280;

  /**
   * Devuelve la URL del logo para usar en la auth.
   *
   * El logo actual (AUREUM ONLINE) ya viene con marco transparente y es un render
   * suave hi-res: NO necesita limpieza de halo. El procesado pixel-art anterior
   * (borrar píxeles claros pegados a transparencia) le comía los bordes plateados
   * y lo dejaba blanco/ruidoso en el login. Se deja como pass-through del PNG crudo,
   * que es lo mismo que usa la pantalla de loading (donde se ve bien).
   */
  public async getProcessedLogoUrl(): Promise<string> {
    return '/assets/logo.png';
  }

  /**
   * Devuelve la URL del logo para el header del login.
   *
   * Antes reescalaba con nearest-neighbor (sin smoothing) para el logo pixel-art.
   * El logo actual es un render suave hi-res: el nearest lo aliaseaba (ruido/blancos).
   * Ahora se devuelve el PNG crudo y el escalado lo hace el CSS con interpolación
   * suave (`imageRendering: auto`), igual que en la pantalla de loading.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async getLogoUrlNearestScaled(_targetWidthPx: number): Promise<string> {
    return this.getProcessedLogoUrl();
  }

  /**
   * Crea una ventana UI con 9 piezas (todas 64x64):
   * - esquinas: fijas
   * - bordes: se repiten (NO se estiran)
   * - centro: bg_tile repetible
   *
   * Devuelve:
   * - root: contenedor con el marco + fondo
   * - content: contenedor donde se pone el contenido real (formularios, etc.)
   */
  public createWindowFrame(
    tilePx: number = AuthChrome.UI_WINDOW_TILE_PX
  ): { root: HTMLDivElement; content: HTMLDivElement } {
    const t = tilePx;

    const root = document.createElement('div');
    root.style.display = 'inline-grid';
    root.style.gridTemplateColumns = `${t}px auto ${t}px`;
    root.style.gridTemplateRows = `${t}px auto ${t}px`;
    root.style.alignItems = 'stretch';
    root.style.justifyItems = 'stretch';
    root.style.boxSizing = 'border-box';
    (root.style as any).imageRendering = 'pixelated';

    const makeCell = (bgUrl: string, repeat: string): HTMLDivElement => {
      const d = document.createElement('div');
      d.style.width = `${t}px`;
      d.style.height = `${t}px`;
      d.style.backgroundImage = `url('${bgUrl}')`;
      d.style.backgroundRepeat = repeat;
      d.style.backgroundSize = `${t}px ${t}px`;
      d.style.backgroundPosition = '0 0';
      return d;
    };

    const makeEdge = (bgUrl: string, repeat: string): HTMLDivElement => {
      const d = document.createElement('div');
      d.style.backgroundImage = `url('${bgUrl}')`;
      d.style.backgroundRepeat = repeat; // repeat-x / repeat-y
      d.style.backgroundSize = `${t}px ${t}px`;
      d.style.backgroundPosition = '0 0';
      return d;
    };

    const tl = makeCell(AuthChrome.UI_WINDOW_ASSETS.cornerTL, 'no-repeat');
    const tr = makeCell(AuthChrome.UI_WINDOW_ASSETS.cornerTR, 'no-repeat');
    const bl = makeCell(AuthChrome.UI_WINDOW_ASSETS.cornerBL, 'no-repeat');
    const br = makeCell(AuthChrome.UI_WINDOW_ASSETS.cornerBR, 'no-repeat');

    const top = makeEdge(AuthChrome.UI_WINDOW_ASSETS.borderTop, 'repeat-x');
    top.style.height = `${t}px`;
    const bottom = makeEdge(AuthChrome.UI_WINDOW_ASSETS.borderBottom, 'repeat-x');
    bottom.style.height = `${t}px`;

    const left = makeEdge(AuthChrome.UI_WINDOW_ASSETS.borderLeft, 'repeat-y');
    left.style.width = `${t}px`;
    const right = makeEdge(AuthChrome.UI_WINDOW_ASSETS.borderRight, 'repeat-y');
    right.style.width = `${t}px`;

    const center = document.createElement('div');
    center.style.backgroundImage = `url('${AuthChrome.UI_WINDOW_ASSETS.bgTile}')`;
    center.style.backgroundRepeat = 'repeat';
    center.style.backgroundSize = `${t}px ${t}px`;
    center.style.backgroundPosition = '0 0';
    center.style.boxSizing = 'border-box';

    const content = document.createElement('div');
    content.style.width = '100%';
    content.style.height = '100%';
    content.style.boxSizing = 'border-box';
    content.style.position = 'relative';
    center.appendChild(content);

    // Grid placement
    tl.style.gridColumn = '1'; tl.style.gridRow = '1';
    top.style.gridColumn = '2'; top.style.gridRow = '1';
    tr.style.gridColumn = '3'; tr.style.gridRow = '1';
    left.style.gridColumn = '1'; left.style.gridRow = '2';
    center.style.gridColumn = '2'; center.style.gridRow = '2';
    right.style.gridColumn = '3'; right.style.gridRow = '2';
    bl.style.gridColumn = '1'; bl.style.gridRow = '3';
    bottom.style.gridColumn = '2'; bottom.style.gridRow = '3';
    br.style.gridColumn = '3'; br.style.gridRow = '3';

    root.append(tl, top, tr, left, center, right, bl, bottom, br);
    return { root, content };
  }
  public showCharacterLoadingBar(centerStage: HTMLElement, nameSlot: HTMLElement): void {
    const barWrap = document.createElement('div');
    barWrap.style.position = 'absolute';
    barWrap.style.left = '50%';
    // Centro vertical del slot del nombre (ya renderizado): la barra queda justo
    // donde estaba el nombre, sin alterar el layout de la columna.
    const y = nameSlot.offsetTop + nameSlot.offsetHeight / 2;
    barWrap.style.top = `${y}px`;
    barWrap.style.transform = 'translate(-50%, -50%)';
    barWrap.style.width = '520px';
    barWrap.style.maxWidth = '60vw';
    barWrap.style.opacity = '0';
    barWrap.style.transition = 'opacity 400ms ease-out';

    const bar = createDomLoadingBar();
    bar.el.style.width = '100%';
    // "Respira" hasta que llegue el progreso real del juego (setProgress es
    // monotónico, así que no retrocede cuando main.ts empieza a alimentarla).
    bar.autoAdvanceTo(0.15, 600);
    barWrap.appendChild(bar.el);
    centerStage.appendChild(barWrap);
    this.host.transitionBar = bar;

    // Fade-in en el siguiente frame (una vez aplicado el opacity:0 inicial).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        barWrap.style.opacity = '1';
      });
    });
  }

  public fadeBgOverlay(opacity: number, ms: number): Promise<void> {
    const el = this.host.bgFadeOverlay;
    if (!el) return Promise.resolve();
    return new Promise((resolve) => {
      el.style.transition = `opacity ${ms}ms ease-in-out`;
      // Forzar reflow para que un cambio inmediato de opacity anime desde el valor actual.
      void el.offsetWidth;
      el.style.opacity = String(opacity);
      window.setTimeout(resolve, ms);
    });
  }

  /**
   * Inicializa el renderizador del mapa de fondo con la configuración de la pantalla de login.
   * Retorna una Promise que se resuelve cuando el fondo está completamente cargado.
   */
  public initMapBackground(): Promise<void> {
    const bgConfig = getRandomLoginBackground();
    return this.loadBackground(bgConfig);
  }

  /**
   * Carga un fondo específico.
   * Si el fondo ya está cargado con la misma configuración, no hace nada.
   * Retorna una Promise que se resuelve cuando el fondo está listo.
   */
  public async loadBackground(bgConfig: AuthBackgroundDef): Promise<void> {
    if (!this.host.mapBackgroundContainer) {
      return;
    }

    // Si ya tenemos el mismo fondo cargado, no recargar
    if (
      this.currentBackground &&
      this.currentBackground.mapId === bgConfig.mapId &&
      this.currentBackground.centerX === bgConfig.centerX &&
      this.currentBackground.centerY === bgConfig.centerY &&
      this.host.mapBackgroundRenderer?.isActive
    ) {
      return;
    }

    // Obtener el MapData para el mapa especificado. Es async porque map1.json se fetchea en
    // runtime (ya no se bundlea); memoizado, así que suele resolver instantáneo. Se resuelve
    // ANTES de destruir el renderer anterior para no dejar el fondo en negro mientras baja.
    const mapData = await getMapData(bgConfig.mapId);

    // El contenedor pudo desmontarse mientras se resolvía el fetch (navegación de pantalla).
    if (!this.host.mapBackgroundContainer) {
      return;
    }

    // Destruir renderer anterior si existe
    if (this.host.mapBackgroundRenderer) {
      this.host.mapBackgroundRenderer.destroy();
      this.host.mapBackgroundRenderer = null;
    }

    this.currentBackground = bgConfig;

    await new Promise<void>((resolve) => {
      this.host.mapBackgroundRenderer = new MapBackgroundRenderer(this.host.mapBackgroundContainer!, {
        mapData,
        centerTileX: bgConfig.centerX,
        centerTileY: bgConfig.centerY,
        ambientCrowd: false, // sin simulación de personajes en el login (pedido del usuario)
        onProgress: (p: number) => {
          this.host.loadingBar?.setProgress(p);
        },
        onReady: () => {
          // El fondo está completamente cargado y renderizado
          resolve();
        },
      });

      this.host.mapBackgroundRenderer.start();
    });
  }

  /**
   * Cambia el fondo a la configuración de una pantalla específica (cambio de pantalla
   * → dip a negro de pantalla completa). `whileHidden` (opcional) se ejecuta MIENTRAS la
   * pantalla está tapada en negro: ahí el caller monta el contenido nuevo, así el
   * fade-out revela el fondo + el contenido ya listos (sin que el form aparezca a medio
   * cargar después del efecto).
   */
  public async changeBackgroundForScreen(
    screen: AuthScreenType,
    whileHidden?: () => void | Promise<void>
  ): Promise<void> {
    const bgConfig = getBackgroundForScreen(screen);
    await this.changeBackground(bgConfig, { fullScreen: true, whileHidden });
  }

  /**
   * Cambia el fondo a la posición de un personaje específico.
   * Muestra indicador de carga durante la transición.
   */
  public async changeBackgroundForCharacter(character: CharacterSummary): Promise<void> {
    const bgConfig = getBackgroundForCharacter(character.mapId, character.x, character.y);
    await this.changeBackground(bgConfig);
  }

  /**
   * Cambia el fondo con cross-fade (dip a negro). Dos modos según la intención:
   *
   * - **`fullScreen` (cambio de pantalla: login→selección, →creación)**: dip de PANTALLA
   *   COMPLETA con el indicador de carga (z-100, cubre fondo + UI). Se funde a negro,
   *   recién ahí se recarga el renderer (sin flash del destroy), corre `whileHidden`
   *   (el caller monta el contenido nuevo bajo el negro) y se funde de vuelta revelando
   *   fondo + contenido ya listos. Cubre el tiempo de carga del form.
   * - **bg-only (cambio de personaje en la lista)**: dip SOLO del fondo (overlay z-5,
   *   debajo de la UI), así la lista sigue visible mientras el mapa se reposiciona.
   *
   * No-op (sin fade) si el fondo destino es idéntico al actual y no hay `whileHidden`
   * que ejecutar: evita el doble fade al entrar a la lista (la pantalla ya funde al
   * fondo del 1er personaje y su auto-selección cae acá sin re-disparar).
   */
  public async changeBackground(
    bgConfig: AuthBackgroundDef,
    opts: { fullScreen?: boolean; whileHidden?: () => void | Promise<void> } = {}
  ): Promise<void> {
    const identical =
      !!this.currentBackground &&
      this.currentBackground.mapId === bgConfig.mapId &&
      this.currentBackground.centerX === bgConfig.centerX &&
      this.currentBackground.centerY === bgConfig.centerY &&
      !!this.host.mapBackgroundRenderer?.isActive;
    if (identical && !opts.whileHidden) return;

    const FADE = AuthChrome.BG_FADE_MS;
    const token = ++this.host.bgTransitionSeq;
    const sameMap =
      !!this.currentBackground &&
      this.currentBackground.mapId === bgConfig.mapId &&
      !!this.host.mapBackgroundRenderer?.isActive;

    if (opts.fullScreen) {
      // Dip de pantalla completa con el indicador de carga (tapa fondo + UI).
      this.showLoadingIndicator(FADE);
      // +80ms de colchón: el fade-in arranca tras un doble rAF → garantizamos negro
      // 100% opaco antes de tocar el renderer (sin flash).
      await new Promise<void>((r) => window.setTimeout(r, FADE + 80));
      if (!identical) {
        if (sameMap) {
          this.host.mapBackgroundRenderer!.updateCenter(bgConfig.centerX, bgConfig.centerY);
          this.currentBackground = bgConfig;
        } else {
          await this.loadBackground(bgConfig);
        }
      }
      // Montar el contenido nuevo MIENTRAS sigue el negro (cubre su tiempo de carga).
      await opts.whileHidden?.();
      this.hideLoadingIndicator();
      return;
    }

    // bg-only: el fondo se funde a negro DETRÁS de la UI; la lista sigue visible.
    await this.fadeBgOverlay(1, FADE);
    if (token !== this.host.bgTransitionSeq) return; // un cambio más nuevo se encarga
    if (sameMap) {
      this.host.mapBackgroundRenderer!.updateCenter(bgConfig.centerX, bgConfig.centerY);
      this.currentBackground = bgConfig;
    } else {
      await this.loadBackground(bgConfig); // el overlay negro tapa el reload del canvas
      if (token !== this.host.bgTransitionSeq) return;
    }
    await this.fadeBgOverlay(0, FADE);
  }

  public showLoadingIndicator(fadeInMs = 0): void {
    const indicator = document.createElement('div');
    indicator.id = 'auth-loading';
    indicator.style.position = 'absolute';
    indicator.style.inset = '0';
    indicator.style.display = 'flex';
    indicator.style.alignItems = 'center';
    indicator.style.justifyContent = 'center';
    indicator.style.backgroundColor = '#000000';
    indicator.style.zIndex = '100';
    indicator.style.transition = `opacity ${fadeInMs > 0 ? fadeInMs : 300}ms ease-out`;
    if (fadeInMs > 0) indicator.style.opacity = '0';

    // Stack vertical: logo grande arriba + barra abajo. Antes el loading de auth NO llevaba
    // el logo grande y se apoyaba en el logo header fijo (#aureum-link-logo); ese header se
    // ocultó (a pedido), así que el loading inicial ahora muestra el logo GRANDE, igual que
    // el loading in-game. Ver docs/UI_SKINNING.md.
    const stack = document.createElement('div');
    stack.style.display = 'flex';
    stack.style.flexDirection = 'column';
    stack.style.alignItems = 'center';
    stack.style.gap = `${LOADING_LAYOUT.gapPx}px`;

    // Logo grande (render suave hi-res, escalado por CSS con interpolación suave).
    const logo = document.createElement('img');
    logo.src = '/assets/logo.png';
    logo.draggable = false;
    logo.style.width = loadingLogoWidthCss();
    logo.style.height = 'auto';
    logo.style.imageRendering = 'auto';
    logo.style.filter = LOGO_GLOW_FILTER;
    stack.appendChild(logo);

    // Barra de carga determinada (frame + fill), misma estética que la LoadingBar in-game.
    // El progreso real lo alimenta loadBackground() vía el onProgress del renderer.
    const bar = createDomLoadingBar();
    this.host.loadingBar = bar;
    stack.appendChild(bar.el);
    indicator.appendChild(stack);
    this.host.root.appendChild(indicator);
    this.host.loadingIndicator = indicator;

    // Arrancar el fade-in en el siguiente frame, una vez que el navegador aplicó el
    // opacity:0 inicial (si no, salta directo a 1 sin animar).
    if (fadeInMs > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          indicator.style.opacity = '1';
        });
      });
    }
  }

  /**
   * Oculta el indicador de carga con una transición suave.
   */
  public hideLoadingIndicator(): void {
    // Completar la barra antes del fade (por si onReady llegó sin un último progress).
    this.host.loadingBar?.setProgress(1);
    this.host.loadingBar = null;
    if (this.host.loadingIndicator) {
      this.host.loadingIndicator.style.opacity = '0';
      setTimeout(() => {
        this.host.loadingIndicator?.remove();
        this.host.loadingIndicator = null;
      }, 300);
    }
  }

  public playerNamesFontFamily(): string {
    return GAME_FONT_FAMILY;
  }

  /**
   * Diálogo de confirmación propio (NO usa `confirm()` nativo, que bloquea la
   * extensión de browser automation y los eventos de la página). Resuelve `true`
   * si el usuario confirma, `false` si cancela / cierra. Con `okOnly` es un aviso
   * de un solo botón que siempre resuelve `true`.
   */
  public confirmDialog(
    message: string,
    title: string,
    opts: { okOnly?: boolean } = {}
  ): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const done = (value: boolean): void => {
        if (settled) return;
        settled = true;
        overlay.remove();
        resolve(value);
      };

      const overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.background = 'rgba(0,0,0,0.7)';
      overlay.style.display = 'flex';
      overlay.style.alignItems = 'center';
      overlay.style.justifyContent = 'center';
      overlay.style.zIndex = '10000';

      const panel = document.createElement('div');
      panel.style.background = '#222';
      panel.style.padding = '24px';
      panel.style.borderRadius = '8px';
      panel.style.minWidth = '320px';
      panel.style.maxWidth = '460px';
      panel.style.boxShadow = '0 4px 12px rgba(0,0,0,0.6)';
      panel.onclick = (ev) => ev.stopPropagation();

      const titleEl = document.createElement('h3');
      titleEl.textContent = title;
      titleEl.style.margin = '0 0 12px 0';

      const messageEl = document.createElement('p');
      messageEl.textContent = message;
      messageEl.style.fontSize = '13px';
      messageEl.style.color = '#ddd';
      messageEl.style.lineHeight = '1.4';

      const buttonRow = document.createElement('div');
      buttonRow.style.display = 'flex';
      buttonRow.style.justifyContent = 'flex-end';
      buttonRow.style.gap = '10px';
      buttonRow.style.marginTop = '16px';

      if (opts.okOnly) {
        const okBtn = createButton('Cerrar');
        okBtn.onclick = () => done(true);
        buttonRow.append(okBtn);
      } else {
        const cancelBtn = createButton('Cancelar', { variant: 'ghost' });
        cancelBtn.onclick = () => done(false);
        const confirmBtn = createButton('Eliminar');
        confirmBtn.style.color = '#f08a8a';
        confirmBtn.onclick = () => done(true);
        buttonRow.append(cancelBtn, confirmBtn);
      }

      panel.append(titleEl, messageEl, buttonRow);
      overlay.appendChild(panel);
      // Click fuera del panel = cancelar (o cerrar el aviso).
      overlay.onclick = () => done(false);
      this.host.root.appendChild(overlay);
    });
  }

  /**
   * Muestra un diálogo simple de opciones desde la pantalla de selección de personajes.
   * Por ahora solo informa los atajos de teclado básicos y sirve como placeholder
   * para futuros ajustes (audio, vídeo, etc.).
   */
  public showOptionsDialog(): void {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.background = 'rgba(0,0,0,0.7)';
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.zIndex = '10000';

    // Label con la URL del sitio en la parte superior centrado
    const aureumLabel = document.createElement('div');
    aureumLabel.textContent = SITE_URL;
    aureumLabel.style.position = 'fixed';
    aureumLabel.style.top = '0';
    aureumLabel.style.left = '50%';
    aureumLabel.style.transform = 'translateX(-50%)';
    aureumLabel.style.padding = '12px';
    aureumLabel.style.color = '#eee';
    aureumLabel.style.fontSize = '14px';
    aureumLabel.style.fontFamily = 'system-ui, sans-serif';
    aureumLabel.style.zIndex = '10001';
    overlay.appendChild(aureumLabel);

    const panel = document.createElement('div');
    panel.style.background = '#222';
    panel.style.padding = '24px';
    panel.style.borderRadius = '8px';
    panel.style.minWidth = '360px';
    panel.style.maxWidth = '480px';
    panel.style.boxShadow = '0 4px 12px rgba(0,0,0,0.6)';

    const title = document.createElement('h3');
    title.textContent = 'Opciones';
    title.style.marginTop = '0';
    title.style.marginBottom = '12px';

    const content = document.createElement('div');
    content.style.fontSize = '13px';
    content.style.color = '#ddd';

    const p = document.createElement('p');
    p.textContent = 'Controles básicos dentro del juego:';

    const ul = document.createElement('ul');
    ul.style.paddingLeft = '18px';

    const addLi = (text: string): void => {
      const li = document.createElement('li');
      li.textContent = text;
      ul.appendChild(li);
    };

    addLi('Movimiento: Flechas (↑ ↓ ← →)');
    addLi('Inventario: I');
    addLi('Chat: Enter para escribir / Enter para enviar / Esc para cancelar');
    addLi('Menú de pausa: Esc');

    const note = document.createElement('p');
    note.style.marginTop = '12px';
    note.style.fontSize = '12px';
    note.style.color = '#aaa';
    note.textContent = 'Más opciones de vídeo, sonido y controles se añadirán en futuras versiones.';

    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Cerrar';
    closeBtn.style.marginTop = '16px';
    closeBtn.style.padding = '6px 12px';
    closeBtn.style.cursor = 'pointer';

    closeBtn.onclick = () => {
      overlay.remove();
    };

    content.append(p, ul, note, closeBtn);
    panel.append(title, content);

    // Evitar que los clicks dentro del panel cierren el overlay
    panel.onclick = (ev) => {
      ev.stopPropagation();
    };

    overlay.appendChild(panel);

    // Cerrar al hacer click fuera del panel
    overlay.onclick = () => {
      overlay.remove();
    };

    this.host.root.appendChild(overlay);
  }
}
